import { useLocation, Navigate, Outlet } from "react-router-dom";
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { menuPermissionsAPI, config } from '../config';

const RequireAuth = () => {
    const location = useLocation();
    const user = JSON.parse(localStorage.getItem('user'));
    const [permissions, setPermissions] = useState(null);
    const [loading, setLoading] = useState(true);

    const isSuperAdmin = user?.role_id == 34;
    const positionId = user?.position || user?.txtPosition;

    useEffect(() => {
        if (!user) {
            setLoading(false);
            return;
        }

        if (isSuperAdmin) {
            setLoading(false);
            return;
        }

        if (positionId) {
            axios.get(`${menuPermissionsAPI}/by-position/${positionId}`, {
                headers: config.headers,
            })
                .then((response) => {
                    setPermissions(response.data.result || []);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("Failed to load RequireAuth menu permissions:", error);
                    setPermissions([]);
                    setLoading(false);
                });
        } else {
            setPermissions([]);
            setLoading(false);
        }
    }, [positionId, isSuperAdmin]);

    if (!user) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    if (loading) {
        // Return blank or loading state while checking permissions
        return null;
    }

    if (isSuperAdmin) {
        return <Outlet />;
    }

    // List of route mappings to permission keys
    const routePermissions = [
        // HR Management
        { urlPrefix: "/employee/list", key: "employees" },
        { urlPrefix: "/employee/add", key: "employees" },
        { urlPrefix: "/employee/view", key: "employees" },
        { urlPrefix: "/employee-attendance", key: "employee_attendance" },
        { urlPrefix: "/payroll-process", key: "payroll_process" },
        { urlPrefix: "/payslip-report", key: "payslip_report" },
        { urlPrefix: "/yearly-bonus", key: "yearly_bonus" },
        { urlPrefix: "/Leave/approval", key: "leave_approval" },
        { urlPrefix: "/advance-payment/list", key: "advance_payment_report" },

        // Purchase Order
        { urlPrefix: "/purchase-order/create", key: "purchase_order_create" },
        { urlPrefix: "/purchase-order/list", key: "purchase_order_list" },
        { urlPrefix: "/purchase-order/edit", key: "purchase_order_list" },
        { urlPrefix: "/purchase-order/view", key: "purchase_order_list" },
        { urlPrefix: "/termsandconditions/add", key: "po_add_terms" },
        { urlPrefix: "/termsandconditions/list", key: "po_list_terms" },
        { urlPrefix: "/termsandconditions/edit", key: "po_list_terms" },

        // Invoice
        { urlPrefix: "/invoice/create", key: "invoice_create" },
        { urlPrefix: "/invoice/list", key: "invoice_list" },
        { urlPrefix: "/invoice/edit", key: "invoice_list" },
        { urlPrefix: "/invoice/view", key: "invoice_list" },
        { urlPrefix: "/invoiceTermsAndConditions/add", key: "invoice_add_terms" },
        { urlPrefix: "/invoiceTermsAndConditions/list", key: "invoice_list_terms" },
        { urlPrefix: "/invoiceTermsAndConditions/edit", key: "invoice_list_terms" },

        // Masters
        { urlPrefix: "/client/list", key: "clients" },
        { urlPrefix: "/client/add", key: "clients" },
        { urlPrefix: "/client/edit", key: "clients" },
        { urlPrefix: "/project/list", key: "projects" },
        { urlPrefix: "/project/add", key: "projects" },
        { urlPrefix: "/project/edit", key: "projects" },
        { urlPrefix: "/store/list", key: "store" },
        { urlPrefix: "/store/add", key: "store" },
        { urlPrefix: "/store/edit", key: "store" },
        { urlPrefix: "/vendor/list", key: "vendor" },
        { urlPrefix: "/vendor/create", key: "vendor" },
        { urlPrefix: "/vendor/edit", key: "vendor" },
        { urlPrefix: "/payment-request/list", key: "payment_requests" },

        // Configurations
        { urlPrefix: "/config/configuration", key: "configuration" },
        { urlPrefix: "/config/departments-master", key: "departments" },
        { urlPrefix: "/config/positions-master", key: "positions" },
        { urlPrefix: "/manage-allowances", key: "allowances" },
        { urlPrefix: "/add-holiday", key: "holidays" },
        { urlPrefix: "/manage-companies/list", key: "companies" },
        { urlPrefix: "/manage-companies/add", key: "companies" },
        { urlPrefix: "/manage-companies/edit", key: "companies" },
        { urlPrefix: "/config/labour-wages", key: "labour_wages" },
        { urlPrefix: "/config/role-menu-settings", key: "role_menu_permissions" },

        // Accounting
        { urlPrefix: "/reports/project-payment-report", key: "project_payment_report" },
        { urlPrefix: "/payment/list", key: "employee_payment_report" },
        { urlPrefix: "/weeklyReport", key: "weekly_payment_report" },
        { urlPrefix: "/cash-in-hand", key: "cash_in_hand" },

        // Labour Attendance
        { urlPrefix: "/weekly-labour-sheet", key: "site_engineer_weekly_sheet" },

        // Settings
        { urlPrefix: "/weekly-labour-approval", key: "site_engineer_weekly_sheet" },
        { urlPrefix: "/labour-approver-settings", key: "labour_approvals" },
        { urlPrefix: "/advance-relief-approver-settings", key: "advance_relief_approver" },
        { urlPrefix: "/payment-approver-settings", key: "payment_approver" },

        // Reports & Payroll Processing
        { urlPrefix: "/payslip-report", key: "payslip_report" },
        { urlPrefix: "/payroll-process", key: "payroll_process" },
        { urlPrefix: "/yearly-bonus", key: "yearly_bonus" },
        { urlPrefix: "/manage-allowances-add", key: "allowances" },
        { urlPrefix: "/employee-attendance-report", key: "employee_attendance_report" },

        // Reports
        { urlPrefix: "/reports/project-summary", key: "project_summary_report" },
        { urlPrefix: "/reports/project-report", key: "project_timeline_report" },
        { urlPrefix: "/reports/employee-activity-report", key: "employee_activity_report" },
        { urlPrefix: "/reports/employee-attendance-report", key: "employee_attendance_report" },
        { urlPrefix: "/reports/leave-report", key: "leave_report" },
        { urlPrefix: "/user/labour-attend", key: "labour_attendance_report" },
        { urlPrefix: "/weeklyPlanSchedule", key: "weekly_plan_schedule" },

        // General / User Menu
        { urlPrefix: "/dashboard", key: "dashboard" },
        { urlPrefix: "/purchase-work-order-dashboard", key: "po_wo_dashboard" },
        { urlPrefix: "/user/attendance", key: "user_attendance" },
        { urlPrefix: "/user/apply-leave", key: "user_apply_leave" },
        { urlPrefix: "/user/labour-attend", key: "user_labour_attendance" }
    ];

    const currentPath = location.pathname;

    // Find matching rule
    const matchingRule = routePermissions.find(rule => currentPath.startsWith(rule.urlPrefix));

    if (matchingRule) {
        const hasPermission = permissions.some(
            (p) => p.submenu_key === matchingRule.key && p.can_view === 1
        );

        if (!hasPermission) {
            return <Navigate to="/unauthorized" replace />;
        }
    }

    return <Outlet />;
};

export default RequireAuth;